// "Business as Asset" — the bridge from the weekly business sim into the lifecycle.
// After three consecutive profitable weeks the player can REGISTER the business as a
// passive lifecycle asset: the sim keeps running (and keeps paying weekly cashflow),
// but the lifecycle ALSO receives a monthly passive income — the moment a kid moves
// from "I run a business" to "my business pays me."
//
// Pure, deterministic, no UI dependencies. The reducer + UI bridge live elsewhere.

import type { GameState, BusinessInstance } from '../state/types.js';

/** How many consecutive profitable weeks unlock the "Register as Asset" offer. */
export const ASSET_STREAK_WEEKS = 3;

/** Operating profit for a given settled week — same definition the HQ statement
 *  shows: revenue − variable costs (cogs + marketing) − fixed/overhead (fixed + wages).
 *  Living costs, investor payouts, and asset income are deliberately excluded — this
 *  is the *business* line, not the kid's overall week. */
export function businessProfitForWeek(state: GameState, week: number): number {
  let revenue = 0;
  let variable = 0;
  let fixed = 0;
  for (const t of state.ledger) {
    if (t.week !== week) continue;
    if (t.type === 'revenue') revenue += t.amount;
    else if (t.type === 'cogs' || t.type === 'marketing') variable += -t.amount;
    else if (t.type === 'fixed' || t.type === 'wages') fixed += -t.amount;
  }
  return revenue - variable - fixed;
}

export type AssetReadiness = {
  /** The business instance, or null if not found. */
  business: BusinessInstance | null;
  /** True only when the player can register the asset *right now*. */
  ready: boolean;
  /** True if it's already been registered (so we don't re-offer). */
  registered: boolean;
  /** Number of consecutive profitable weeks the business is currently on. */
  streak: number;
  /** Last three settled weeks (most-recent last), with profit per week. */
  recentWeeks: { week: number; profit: number }[];
  /** The monthly passive income the asset would pay if registered now —
   *  per spec, `round(avg(last 3 weeks profit) / 4)`. 0 if not ready. */
  monthlyIncome: number;
  /** Balance-sheet value for the asset (a year of passive income). */
  value: number;
};

/** Inspect a business and tell the UI whether it's ready to be registered as a
 *  lifecycle asset, plus the numbers it would carry if it were. Pure. */
export function businessAssetReadiness(state: GameState, businessId: string): AssetReadiness {
  const business = state.businesses.find((b) => b.id === businessId) ?? null;
  const registered = business?.registeredAsAsset === true;

  // The last N settled weeks, from oldest to most-recent. clock.week is the
  // in-flight week; the most recently settled week is week - 1.
  const recentWeeks: { week: number; profit: number }[] = [];
  for (let i = ASSET_STREAK_WEEKS; i >= 1; i--) {
    const w = state.clock.week - i;
    if (w < 1) continue;
    recentWeeks.push({ week: w, profit: businessProfitForWeek(state, w) });
  }
  const allProfitable = recentWeeks.length === ASSET_STREAK_WEEKS && recentWeeks.every((r) => r.profit > 0);

  const avg = recentWeeks.length ? recentWeeks.reduce((s, r) => s + r.profit, 0) / recentWeeks.length : 0;
  const monthlyIncome = allProfitable ? Math.round(avg / 4) : 0;

  return {
    business,
    ready: !!business && !registered && allProfitable,
    registered,
    streak: countTrailingProfitable(state),
    recentWeeks,
    monthlyIncome,
    value: monthlyIncome * 12, // a year of passive income, the kid's mental anchor
  };
}

/** How many consecutive profitable weeks ending at the most recently settled
 *  week. Useful for "you're 2/3 of the way there" UI nudges, even when the
 *  full streak hasn't yet been hit. */
function countTrailingProfitable(state: GameState): number {
  let n = 0;
  for (let w = state.clock.week - 1; w >= 1; w--) {
    if (businessProfitForWeek(state, w) > 0) n++;
    else break;
  }
  return n;
}
